import { Stack, Typography } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';

import { Button } from '@/components/form/baseInputs/Button';
import { ArrowUpRightIcon } from '@/components/icons/ArrowUpRightIcon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { Callout } from '@/components/notifications/Callout/Callout';
import { FeedbackMessages } from '@/components/notifications/Feedback/useFeedback';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { Column } from '@/components/tables/DataTable/types';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { ROUTE_KEYS } from '@/navigation/constants';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';

import { usePresentationApprovalRequestsTableQuery } from './graphql/PresentationApprovalRequestsTable.generated';
import {
  getPresentationApprovalRows,
  PresentationApprovalRow,
} from './PresentationApprovalRequestsTable.utils';

// The list shows only the current user's open requests, so it stays short.
const MAX_OPEN_REQUESTS = 500;

function useOpenPresentation() {
  const { navigate } = useNavigateToRoute();
  const trackUserEvent = useTrackUserEvent();
  return async (row: PresentationApprovalRow) => {
    await navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2, {
      householdId: row.householdId,
      presentationId: row.id,
    });
    trackUserEvent('presentation approval_request_opened', {
      presentationId: row.id,
      householdId: row.householdId,
    });
  };
}

function ViewPresentationButton({ row }: { row: PresentationApprovalRow }) {
  const openPresentation = useOpenPresentation();
  return (
    <Button
      variant="secondary"
      size="sm"
      endIcon={ArrowUpRightIcon}
      onClick={(event) => {
        // The row also opens the presentation on click.
        event.stopPropagation();
        void openPresentation(row);
      }}
    >
      View presentation
    </Button>
  );
}

function PresentationApprovalRequestsEmptyState() {
  return <TableEmptyState text="No presentations are pending approval." />;
}

const COLUMNS: Column<PresentationApprovalRow>[] = [
  {
    field: 'presentationName',
    headerName: 'Name',
    flex: 1,
    minWidth: 280,
    renderCell: TwoLineTextRenderer({
      lineOne: ({ row }) => row.presentationName,
    }),
  },
  { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 200 },
  {
    field: 'requesterName',
    headerName: 'Requested by',
    flex: 1,
    minWidth: 200,
    renderCell: TwoLineTextRenderer({
      lineOne: ({ row }) => row.requesterName,
      lineTwo: ({ row }) => formatDateToMonDDYYYY(row.requestedAt),
    }),
  },
  {
    // The presentation ID is not shown, so this column uses its field.
    field: 'id',
    headerName: '',
    width: 200,
    align: 'right',
    sortable: false,
    renderCell: ({ row }) => (
      <CellContainer align="right">
        <ViewPresentationButton row={row} />
      </CellContainer>
    ),
  },
];

/**
 * PresentationApprovalRequestsTable lists the presentations that the current
 * user was asked to approve and that wait for approval. It does not list
 * presentations that the user only requested approval for, attestation
 * requests, or presentations that any user already approved.
 */
export function PresentationApprovalRequestsTable() {
  const { reportError } = useReportError();
  const openPresentation = useOpenPresentation();

  const { data, loading, error } = usePresentationApprovalRequestsTableQuery({
    variables: { first: MAX_OPEN_REQUESTS },
    fetchPolicy: 'cache-and-network',
    onError: (error) => {
      reportError('Could not load presentation approval requests', error);
    },
  });

  const requests =
    data?.pendingPresentationApprovalRequests.edges?.flatMap((edge) =>
      edge?.node ? [edge.node] : []
    ) ?? [];
  const rows = getPresentationApprovalRows(requests);

  return (
    <Stack spacing={2}>
      <Typography variant="body-m">
        The following presentations are pending approval.
      </Typography>
      {/* A failed refresh keeps the cached rows, so show the error with them. */}
      {error && (
        <Callout severity="error">{FeedbackMessages.queryError}</Callout>
      )}
      <DataTable
        aria-label="Presentations pending approval"
        rows={rows}
        columns={COLUMNS}
        loading={loading}
        onRowClick={({ row }: GridRowParams<PresentationApprovalRow>) => {
          void openPresentation(row);
        }}
        slots={{
          // A failed query has no rows, but the callout above explains it.
          noRowsOverlay:
            error && !data
              ? () => null
              : PresentationApprovalRequestsEmptyState,
        }}
      />
    </Stack>
  );
}
